// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2CreateProjectButton.test.tsx
import { type AskPunttChat } from "@mg/schemas/src/christo/askPuntt";
import { CircleNotch, FolderPlus } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { usePromoteChatToProject } from "../../utils/queries/askPunttProjects";
import { askV2ProjectNavLink } from "../shell/askV2ChatNavigation";

export function canPromoteChatToProject(
  chat: Pick<AskPunttChat, "projectId" | "workflowType">,
): boolean {
  // why: projectId == null is standalone; workflowType "project" is the legacy chat-as-project the schema still emits
  return chat.projectId == null && chat.workflowType !== "project";
}

export function AskV2CreateProjectButton({ chatId }: { chatId: string }) {
  const navigate = useNavigate();
  const promote = usePromoteChatToProject();
  const { t } = useTranslation("ui");

  const handleClick = async () => {
    try {
      const result = await promote.mutateAsync(chatId);
      void navigate(askV2ProjectNavLink(result.projectId));
    } catch {
      // usePromoteChatToProject surfaces an error toast.
    }
  };

  return (
    <Button
      data-testid="ask-v2-chat-create-project"
      aria-label={t("promoteToProject.ariaLabel")}
      size="1"
      variant="soft"
      color="gray"
      className="shrink-0 cursor-pointer"
      disabled={promote.isPending}
      onClick={() => {
        void handleClick();
      }}
    >
      {promote.isPending ? (
        <CircleNotch size={14} weight="bold" className="animate-spin" />
      ) : (
        <FolderPlus size={14} aria-hidden="true" />
      )}
      <Text size="1">{t("promoteToProject.button")}</Text>
    </Button>
  );
}
